/**
 * Where tokens live in the browser.
 *
 * - The owner token stays in localStorage so the person who created a bridge
 *   can come back to it from the same browser until it expires.
 * - A guest's claim token stays in sessionStorage: a reload must not spend a
 *   second view, but a new tab is a new reader.
 * - The link key is in the link's fragment. Once a page has opened it, it is kept for this tab only, in sessionStorage under
 *   the bridge's id, so that a reload goes on working after the key has been taken out of the address bar (THREAT_MODEL.md:
 *   the address, the history and what a copied address gives are the trade). A new tab, a closed tab and a private window
 *   have none; the key never reaches localStorage, the server or a log.
 */

function safeGet(storage: Storage | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(storage: Storage | undefined, key: string, value: string | null): void {
  try {
    if (value === null) storage?.removeItem(key);
    else storage?.setItem(key, value);
  } catch {
    // private mode or blocked storage: the session simply will not persist
  }
}

function local(): Storage | undefined {
  return typeof localStorage === 'undefined' ? undefined : localStorage;
}

function session(): Storage | undefined {
  return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
}

export function getOwnerToken(bridgeId: string): string | null {
  return safeGet(local(), `bridge.owner.${bridgeId}`);
}

export function setOwnerToken(bridgeId: string, token: string | null): void {
  safeSet(local(), `bridge.owner.${bridgeId}`, token);
}

export function getClaimToken(bridgeId: string): string | null {
  return safeGet(session(), `bridge.claim.${bridgeId}`);
}

export function setClaimToken(bridgeId: string, token: string | null): void {
  safeSet(session(), `bridge.claim.${bridgeId}`, token);
}

/** The link key of a bridge this tab has opened. */
export function getBridgeKey(bridgeId: string): string | null {
  return safeGet(session(), `bridge.key.${bridgeId}`);
}

export function setBridgeKey(bridgeId: string, key: string | null): void {
  safeSet(session(), `bridge.key.${bridgeId}`, key);
}

export type ThemeChoice = 'auto' | 'light' | 'dark';

export function getThemeChoice(): ThemeChoice {
  const value = safeGet(local(), 'bridge.theme');
  return value === 'light' || value === 'dark' ? value : 'auto';
}

export function setThemeChoice(choice: ThemeChoice): void {
  safeSet(local(), 'bridge.theme', choice === 'auto' ? null : choice);
}

/** Why the owner opened a two-way bridge: to talk, or to receive something on this device. */
export type Purpose = 'chat' | 'receive';

export function getPurpose(bridgeId: string): Purpose | null {
  const value = safeGet(local(), `bridge.purpose.${bridgeId}`);
  return value === 'chat' || value === 'receive' ? value : null;
}

export function setPurpose(bridgeId: string, purpose: Purpose | null): void {
  safeSet(local(), `bridge.purpose.${bridgeId}`, purpose);
}
